import { format } from "date-fns";
import { horizontalPositionForTime } from "@produce/shared";
import { useMinuteTick } from "./useMinuteTick";

export interface CurrentTimeMarkerOptions {
  /** Hour the timeline's left edge represents (see `resolveScheduleStartHour`). */
  startHour: number;
  /** Width of one hour in px, zoom already applied. */
  pixelsPerHour: number;
  /** How many hours the timeline spans — used to hide a marker that ran off it. */
  spanHours: number;
  /** Caller's own gate: today ∧ the producer's "show marker" setting. */
  enabled: boolean;
  /** Roll times before 06:00 into the night's tail (default true). */
  afterMidnight?: boolean;
}

export interface CurrentTimeMarker {
  /** Render the marker? False when disabled or the clock is off the timeline. */
  visible: boolean;
  /** Px offset from the timeline's left edge (not including any label column). */
  position: number;
  /** Current wall-clock time as "HH:MM" — format for display at the call site. */
  time: string;
}

/**
 * Live "now" marker for the horizontal schedule timelines.
 *
 * The position is derived during render from the CURRENT `startHour` /
 * `pixelsPerHour`, never captured in an interval closure. That matters: the
 * timeline's start hour is resolved from doors-open + the day's items, which
 * arrive asynchronously, so a position computed once on mount kept pointing at
 * the old anchor — the marker sat a whole hour (or more) off the grid while the
 * blocks around it were correct. The clock only ticks; the layout always
 * follows the props the marker is rendered with.
 */
export function useCurrentTimeMarker({
  startHour,
  pixelsPerHour,
  spanHours,
  enabled,
  afterMidnight = true,
}: CurrentTimeMarkerOptions): CurrentTimeMarker {
  const now = useMinuteTick(enabled);

  const time = format(now, "HH:mm");
  const position = horizontalPositionForTime(time, startHour, pixelsPerHour, { afterMidnight });
  const onTimeline = position >= 0 && position <= spanHours * pixelsPerHour;

  return { visible: enabled && onTimeline, position, time };
}
